import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { FeatureProvider } from "./contexts/FeatureContext";
import { PursuitProvider } from "./contexts/PursuitContext";
import { PerspectiveProvider } from "./contexts/PerspectiveContext";
import { RiskAssessmentProvider } from "./contexts/RiskAssessmentContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PricingPage from "./pages/PricingPage";
import TrialExpiredPage from "./pages/TrialExpiredPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import { LEGAL_PAGES_HIDDEN } from "./lib/version";

// Auth Pages (Registration & Invite)
import ClientRegistrationPage from "./pages/auth/ClientRegistrationPage";
import InviteAcceptancePage from "./pages/auth/InviteAcceptancePage";
import RedirectIfAuthenticated from "./components/auth/RedirectIfAuthenticated";
import VerifyOtpPage from "./pages/VerifyOtpPage";
import SetupAuthenticatorPage from "./pages/SetupAuthenticatorPage";
import VerifyAuthenticatorPage from "./pages/VerifyAuthenticatorPage";

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";

// Dashboard Pages (Shared)
import PursuitPipeline from "./pages/dashboard/PursuitPipelineLive";

// Admin Pages (Super Admin / Platform Owner)
import StrategicDashboard from "./pages/admin/StrategicDashboard";
import ConsultantManagement from "./pages/admin/ConsultantManagement";
import GlobalAssetLibrary from "./pages/admin/GlobalAssetLibrary";
import ContractorLifecycleDashboard from "./pages/admin/ClientLifecycleDashboard";
import AuditLogPage from "./pages/admin/AuditLogPage";
import SystemDocs from "./pages/admin/SystemDocs";
import ProductRoadmapPage from "./pages/admin/ProductRoadmapPage";
import AISupportDashboard from "./pages/admin/AISupportDashboard";
import AgencyPlaybook from "./pages/admin/AgencyPlaybook";
import DevelopmentCostsPage from "./pages/admin/DevelopmentCostsPage";
import UserManagement from "./pages/admin/UserManagement";
import SystemAIConfiguration from "./pages/admin/SystemAIConfiguration";
import FeatureReleaseConsole from "./pages/admin/FeatureReleaseConsole";

// Consultant Pages
import ConsultantClientDirectory from "./pages/consultant/ClientDirectory";
import ClientProfile from "./pages/consultant/ClientProfile";

// Pursuit-Specific Pages
import OpportunityIntakeLive from "./pages/pursuit/OpportunityIntakeLive";
import GoNoGoDashboardLive from "./pages/pursuit/GoNoGoDashboardLive";
import GapAnalysisLive from "./pages/pursuit/GapAnalysisLive";
import ComplianceTrackerLive from "./pages/pursuit/ComplianceTrackerLive";
import ProposalWorkspaceLive from "./pages/pursuit/ProposalWorkspaceLive";
// v3.18.0 — Strategy Stage (Win Theme Registry + Section M Evaluation Factors)
import StrategyWorkspace from "./pages/pursuit/StrategyWorkspace";
// v3.11.8 — SubmissionGuideLive is rendered as an embedded tab inside
// ComplianceTrackerLive, so it's not imported at the route level anymore.
import IngestionHub from "./pages/pursuit/IngestionHub";
import CoverageMapLive from "./pages/pursuit/CoverageMapLive";
// v3.13.0 — Tailored Capability Statement (RFI pursuits)
import CapabilityStatement from "./pages/pursuit/CapabilityStatement";
// v3.14.0 — Questions-for-Agency
import AgencyQuestions from "./pages/pursuit/AgencyQuestions";

// Settings Pages
import PwinConfiguration from "./pages/settings/PwinConfiguration";

// Billing Pages
import SlotTopUpReturn from "./pages/billing/SlotTopUpReturn";
import SubscribePage from "./pages/billing/SubscribePage";
import SubscribeReturn from "./pages/billing/SubscribeReturn";

// Role-aware routers
import DashboardRouter from "./pages/shared/DashboardRouter";
import SettingsRouter from "./pages/shared/SettingsRouter";

// Help Center
import HelpCenter from "./pages/help/HelpCenter";

// Sentinel
import MarketplaceSentinel from "./pages/sentinel/MarketplaceSentinel";

// v3.15.0 — Knowledge Base (Ask the Library)
import KnowledgeBaseLive from "./pages/dashboard/KnowledgeBaseLive";

// Feature gating
import FeatureGate from "./components/FeatureGate";
import ProdModeIndicator from "./components/ProdModeIndicator";

function App() {
  return (
    <AuthProvider>
      <FeatureProvider>
        <PursuitProvider>
        <PerspectiveProvider>
          <RiskAssessmentProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route
                  path="/login"
                  element={
                    <RedirectIfAuthenticated>
                      <LoginPage />
                    </RedirectIfAuthenticated>
                  }
                />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/trial-expired" element={<TrialExpiredPage />} />
                {/* v3.20.0 — Self-serve password reset */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-otp" element={<VerifyOtpPage />} />
                <Route path="/setup-authenticator" element={<SetupAuthenticatorPage />} />
                <Route path="/verify-authenticator" element={<VerifyAuthenticatorPage />} />
                <Route
                  path="/terms"
                  element={LEGAL_PAGES_HIDDEN ? <Navigate to="/" replace /> : <TermsOfUsePage />}
                />
                <Route
                  path="/privacy"
                  element={LEGAL_PAGES_HIDDEN ? <Navigate to="/" replace /> : <PrivacyPolicyPage />}
                />
                
                {/* Registration & Invite routes */}
                <Route
                  path="/register/client"
                  element={
                    <RedirectIfAuthenticated>
                      <ClientRegistrationPage />
                    </RedirectIfAuthenticated>
                  }
                />
                <Route path="/invite/:token" element={<InviteAcceptancePage />} />

                {/* Stripe return URLs.
                    These MUST live at top-level (no workspace prefix) because the
                    `success_url` we send to Stripe is `{window.location.origin}/billing/...`,
                    which has no tenant slug. The workspace-scoped duplicates below are
                    kept only as fallbacks in case a bookmarked URL is hit. */}
                <Route path="/billing/slots/return" element={<SlotTopUpReturn />} />
                <Route path="/billing/subscribe/return" element={<SubscribeReturn />} />

                {/* Super Admin routes (Platform Owner) - Streamlined */}
                <Route path="/admin" element={<DashboardLayout />}>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<StrategicDashboard />} />
                  <Route path="lifecycle" element={<ContractorLifecycleDashboard />} />
                  <Route path="consultants" element={<ConsultantManagement />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="agencies" element={<AgencyPlaybook />} />
                  <Route path="assets" element={<GlobalAssetLibrary />} />
                  <Route path="audit" element={<AuditLogPage />} />
                  <Route path="docs" element={<SystemDocs />} />
                  <Route path="ai-support" element={<AISupportDashboard />} />
                  <Route path="roadmap" element={<ProductRoadmapPage />} />
                  <Route path="costs" element={<DevelopmentCostsPage />} />
                  <Route path="ai-config" element={<SystemAIConfiguration />} />
                  <Route path="features" element={<FeatureReleaseConsole />} />
                </Route>

                {/* Tenant workspace routes (Consultant + Client) - Role-aware */}
                <Route path="/:workspaceId" element={<DashboardLayout />}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardRouter />} />
                  <Route path="sentinel" element={<FeatureGate featureId="sentinel"><MarketplaceSentinel /></FeatureGate>} />
                  <Route path="clients" element={<ConsultantClientDirectory />} />
                  <Route path="clients/:clientId" element={<ClientProfile />} />
                  <Route path="pipeline" element={<PursuitPipeline />} />
                  <Route path="knowledge-base" element={<KnowledgeBaseLive />} />
                  <Route path="manage-documents" element={<Navigate to="../knowledge-base" replace />} />
                  <Route path="settings" element={<SettingsRouter />} />
                  <Route path="help" element={<HelpCenter />} />
                  
                  {/* Pursuit-Specific Routes (Live with Auto-Save) */}
                  <Route path="pursuit/:pursuitId">
                    <Route index element={<Navigate to="intake" replace />} />
                    <Route path="intake" element={<FeatureGate featureId="opportunity_intake"><OpportunityIntakeLive /></FeatureGate>} />
                    <Route path="ingestion" element={<FeatureGate featureId="document_ingestion"><IngestionHub /></FeatureGate>} />
                    <Route path="gonogo" element={<FeatureGate featureId="go_no_go"><GoNoGoDashboardLive /></FeatureGate>} />
                    <Route path="go-no-go" element={<Navigate to="../gonogo" replace />} />
                    <Route path="gaps" element={<FeatureGate featureId="gap_analysis"><GapAnalysisLive /></FeatureGate>} />
                    <Route path="gap-analysis" element={<Navigate to="../gaps" replace />} />
                    <Route path="compliance" element={<FeatureGate featureId="compliance_tracker"><ComplianceTrackerLive /></FeatureGate>} />
                    {/* v3.11.8 — /submission-guide now redirects into the
                        Submission Day tab of Compliance Tracker so there's a
                        single URL/source-of-truth while preserving deep-links. */}
                    <Route path="submission-guide" element={<Navigate to="../compliance?tab=submission-day" replace />} />
                    <Route path="proposal" element={<FeatureGate featureId="proposal_workspace"><ProposalWorkspaceLive /></FeatureGate>} />
                    <Route path="proposal-workspace" element={<Navigate to="../proposal" replace />} />
                    {/* v3.18.0 — Strategy Stage */}
                    <Route path="strategy" element={<StrategyWorkspace />} />
                    <Route path="coverage-map" element={<CoverageMapLive />} />
                    {/* v3.13.0 — Tailored Capability Statement (RFI pursuits) */}
                    <Route path="capability-statement" element={<CapabilityStatement />} />
                    {/* v3.14.0 — Questions-for-Agency */}
                    <Route path="questions" element={<AgencyQuestions />} />
                  </Route>
                  
                  {/* Settings Routes */}
                  <Route path="settings/pwin-config" element={<PwinConfiguration />} />
                  <Route path="billing/slots/return" element={<SlotTopUpReturn />} />
                  <Route path="billing/subscribe" element={<SubscribePage />} />
                  <Route path="billing/subscribe/return" element={<SubscribeReturn />} />
                </Route>

                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" />
            <ProdModeIndicator />
          </div>
        </RiskAssessmentProvider>
        </PerspectiveProvider>
      </PursuitProvider>
      </FeatureProvider>
    </AuthProvider>
  );
}

export default App;
